// src/pages/Users.js
//
// v2 :
//  • Barre de recherche (nom, prénom, pseudo, e-mail — accents ignorés) +
//    filtre par rôle + filtre « Doublons possibles ».
//  • Détection des doublons : même prénom + nom (ordre et accents ignorés) et
//    dates de naissance compatibles (src/utils/studentIdentity.js). Les
//    doublons sont signalés, jamais supprimés automatiquement : la colonne
//    « Inscriptions » et la date de création aident à choisir lequel garder.
//  • SYNCHRONISATION users → teachers / students / parents CORRIGÉE :
//      - elle ne réécrit plus les champs par défaut sur les fiches existantes
//        (avant : parents.children remis à [] et phone / specialties /
//        medicalInfo… vidés à CHAQUE ouverture de cette page) ;
//      - elle ne SUPPRIME plus les fiches absentes de users (les élèves
//        ajoutés depuis les présences ou le site n'ont pas de compte) ;
//      - isAdult est un vrai booléen (avant : une fonction, ce qui faisait
//        échouer toute la synchro des élèves) ;
//      - elle n'écrit que ce qui a changé (au lieu de tout réécrire).

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  setDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector
} from '@mui/x-data-grid';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Paper,
  Stack,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  PhoneAndroid as AppIcon,
  Language as WebIcon,
  AdminPanelSettings as AdminIcon,
  Search as SearchIcon,
  ContentCopy as DuplicateIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { normalizeText } from '../utils/billing';
import { parseBirthDate, recordBirth, groupPeople } from '../utils/studentIdentity';

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

const STUDENT_ROLES = ['Élève', 'Élève adulte'];
const ROLES = ['Professeur', 'Élève', 'Élève adulte', 'Parent', 'Admin'];

// ── Synchronisation non destructive users → collection de rôle ─────────────
const sameDate = (a, b) => {
  const da = parseBirthDate(a);
  const dbb = parseBirthDate(b);
  if (!da && !dbb) return true;
  if (!da || !dbb) return false;
  return Math.abs(da.getTime() - dbb.getTime()) < 1000;
};

async function syncWithCollection(users, collectionName, defaultFields) {
  try {
    const existingSnap = await getDocs(collection(db, collectionName));
    const existing = new Map(existingSnap.docs.map((d) => [d.id, d.data()]));

    await Promise.all(users.map(async (user) => {
      const identity = {
        firstName: user.firstName,
        lastName:  user.lastName,
        email:     user.email,
        pseudo:    user.pseudo,
        role:      user.role,
      };
      if (user.birthDateObj) identity.dateOfBirth = Timestamp.fromDate(user.birthDateObj);
      if (STUDENT_ROLES.includes(user.role)) identity.isAdult = user.role === 'Élève adulte';

      const current = existing.get(user.id);
      const ref = doc(db, collectionName, user.id);

      if (!current) {
        // Nouvelle fiche : valeurs par défaut + identité
        await setDoc(ref, { ...defaultFields, ...identity, userId: user.id, lastSynced: serverTimestamp() }, { merge: true });
        return;
      }

      // Fiche existante : uniquement les champs d'identité qui ont changé
      const changes = {};
      Object.entries(identity).forEach(([k, v]) => {
        if (k === 'dateOfBirth') { if (!sameDate(v, current.dateOfBirth)) changes[k] = v; return; }
        if ((v ?? '') !== (current[k] ?? '')) changes[k] = v;
      });
      if (Object.keys(changes).length === 0) return;
      await setDoc(ref, { ...changes, lastSynced: serverTimestamp() }, { merge: true });
    }));
  } catch (error) {
    console.error(`Erreur lors de la synchronisation avec ${collectionName}:`, error);
  }
}

async function syncAllRoleCollections(users) {
  await syncWithCollection(users.filter((u) => u.role === 'Professeur'), 'teachers', {
    phone: '', specialties: '', availability: '',
  });
  await syncWithCollection(users.filter((u) => STUDENT_ROLES.includes(u.role)), 'students', {
    phone: '', level: '', emergencyContact: '', medicalInfo: '',
  });
  await syncWithCollection(users.filter((u) => u.role === 'Parent'), 'parents', {
    phone: '', children: [], address: '',
  });
}

// ── Composant ────────────────────────────────────────────────────────────

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [onlyDuplicates, setOnlyDuplicates] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Inscriptions : source « site » + nombre d'inscriptions liées à chaque compte
        const regsSnap = await getDocs(collection(db, 'registrations'));
        const siteEmails = new Set();
        const regCount = new Map();
        const bump = (id) => { if (id) regCount.set(id, (regCount.get(id) || 0) + 1); };
        regsSnap.docs.forEach((d) => {
          const data = d.data();
          const email = data.contactInfo?.email || data.parentInfo?.email || data.email;
          if (email && data.source !== 'mobile_app') siteEmails.add(email.toLowerCase());
          const linked = new Set([data.appUserId, data.targetStudentId, ...(data.persons || []).map((p) => p.studentId)]);
          linked.forEach(bump);
        });

        const querySnapshot = await getDocs(collection(db, 'users'));
        const usersList = querySnapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          const birthDateObj = parseBirthDate(data.dateOfBirth || data.birthday || data.dateOfExth || data.birthDate);

          const isApp = data.registrationSource === 'mobile_app' ||
                        data.emailVerificationRequired === true ||
                        (data.pseudo && data.pseudo.trim() !== '');
          const isFromSite = !isApp && siteEmails.has((data.email || '').toLowerCase());
          const created = data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt ? new Date(data.createdAt) : null);

          return {
            id: docSnap.id,
            pseudo: data.pseudo || '',
            email: data.email || '',
            role: data.role || '',
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            dateOfBirth: birthDateObj ? birthDateObj.toLocaleDateString('fr-FR') : '',
            birthDateObj,
            birthKey: recordBirth(data),
            source: isApp ? 'app' : isFromSite ? 'site' : 'admin',
            registrations: regCount.get(docSnap.id) || 0,
            createdAt: created && !Number.isNaN(created.getTime()) ? created : null,
          };
        });

        // Doublons possibles
        const groups = groupPeople(usersList.map((u) => ({
          id: u.id, firstName: u.firstName, lastName: u.lastName, birth: u.birthKey,
        })));
        const dupInfo = new Map();
        groups.forEach((g) => {
          if (g.length < 2) return;
          const key = g.map((r) => r.id).sort()[0];
          g.forEach((r) => dupInfo.set(r.id, { key, count: g.length }));
        });
        usersList.forEach((u) => {
          const info = dupInfo.get(u.id);
          u.dupKey = info?.key || null;
          u.dupCount = info?.count || 0;
        });

        setUsers(usersList);
        setLoading(false);
        // Synchro en arrière-plan : n'empêche plus l'affichage de la page
        syncAllRoleCollections(usersList);
      } catch (error) {
        console.error('Erreur de chargement des utilisateurs:', error);
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleDelete = async (id) => {
    const u = users.find((x) => x.id === id);
    const warn = u?.registrations
      ? `\n\nAttention : ${u.registrations} inscription(s) sont liées à ce compte.`
      : '';
    if (!window.confirm(`Supprimer l'utilisateur ${u ? `${u.firstName} ${u.lastName}` : ''} ?${warn}\n\n(Le compte de connexion Firebase Auth n'est pas supprimé.)`)) return;
    try {
      await deleteDoc(doc(db, 'users', id));
      if (u) {
        if (u.role === 'Professeur') await deleteDoc(doc(db, 'teachers', id));
        else if (STUDENT_ROLES.includes(u.role)) await deleteDoc(doc(db, 'students', id));
        else if (u.role === 'Parent') await deleteDoc(doc(db, 'parents', id));
      }
      setUsers((prev) => {
        const next = prev.filter((x) => x.id !== id);
        // Recalcul du compteur de doublons du groupe concerné
        if (u?.dupKey) {
          const left = next.filter((x) => x.dupKey === u.dupKey);
          left.forEach((x) => {
            x.dupCount = left.length > 1 ? left.length : 0;
            if (left.length < 2) x.dupKey = null;
          });
        }
        return [...next];
      });
    } catch (error) {
      console.error('Erreur de suppression:', error);
    }
  };

  const duplicateCount = useMemo(() => new Set(users.filter((u) => u.dupKey).map((u) => u.dupKey)).size, [users]);

  const rows = useMemo(() => {
    const s = normalizeText(search);
    const list = users.filter((u) => {
      if (roleFilter && u.role !== roleFilter) return false;
      if (onlyDuplicates && !u.dupKey) return false;
      if (!s) return true;
      const hay = normalizeText(`${u.firstName} ${u.lastName} ${u.lastName} ${u.firstName} ${u.pseudo} ${u.email} ${u.role}`);
      return s.split(' ').every((w) => hay.includes(w));
    });
    if (onlyDuplicates) {
      list.sort((a, b) => (a.dupKey || '').localeCompare(b.dupKey || '') || `${a.lastName}${a.firstName}`.localeCompare(`${b.lastName}${b.firstName}`));
    }
    return list;
  }, [users, search, roleFilter, onlyDuplicates]);

  const columns = [
    { field: 'firstName', headerName: 'Prénom', width: 130 },
    { field: 'lastName', headerName: 'Nom', width: 130 },
    {
      field: 'dupCount',
      headerName: 'Doublon',
      width: 110,
      renderCell: (params) => (params.value > 1 ? (
        <Tooltip title="Même nom et date de naissance compatible qu'un autre compte">
          <Chip
            icon={<DuplicateIcon style={{ fontSize: 13 }} />}
            label={`×${params.value}`}
            size="small"
            color="warning"
            onClick={() => { setOnlyDuplicates(true); setSearch(`${params.row.firstName} ${params.row.lastName}`); }}
          />
        </Tooltip>
      ) : null),
    },
    { field: 'pseudo', headerName: 'Pseudo', width: 120 },
    { field: 'email', headerName: 'Email', width: 220 },
    {
      field: 'source',
      headerName: 'Source',
      width: 130,
      renderCell: (params) => {
        if (params.value === 'app')
          return <Chip icon={<AppIcon style={{ fontSize: 13 }} />} label="App mobile"
            size="small" color="success" variant="outlined"
            sx={{ '& .MuiChip-label': { fontSize: 11 } }} />;
        if (params.value === 'site')
          return <Chip icon={<WebIcon style={{ fontSize: 13 }} />} label="Site web"
            size="small" color="info" variant="outlined"
            sx={{ '& .MuiChip-label': { fontSize: 11 } }} />;
        return <Chip icon={<AdminIcon style={{ fontSize: 13 }} />} label="Admin"
          size="small" color="warning" variant="outlined"
          sx={{ '& .MuiChip-label': { fontSize: 11 } }} />;
      },
    },
    {
      field: 'role',
      headerName: 'Rôle',
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value || '—'}
          color={
            params.value === 'Professeur' ? 'primary' :
            params.value === 'Élève' ? 'secondary' :
            params.value === 'Élève adulte' ? 'info' :
            params.value === 'Parent' ? 'warning' : 'default'
          }
          variant="outlined"
          size="small"
        />
      )
    },
    {
      field: 'dateOfBirth',
      headerName: 'Naissance',
      width: 110,
      valueFormatter: (params) => params.value || '—'
    },
    {
      field: 'registrations',
      headerName: 'Inscriptions',
      width: 105,
      type: 'number',
    },
    {
      field: 'createdAt',
      headerName: 'Créé le',
      width: 110,
      type: 'date',
      valueFormatter: (params) => (params.value ? params.value.toLocaleDateString('fr-FR') : '—'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <div>
          <Tooltip title="Modifier">
            <IconButton onClick={() => navigate(`/users/${params.id}`)} color="primary">
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Supprimer">
            <IconButton onClick={() => handleDelete(params.id)} color="error">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1, color: '#7C3AED' }} />
            Gestion des utilisateurs
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/users/new')}
            sx={{ backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' } }}
          >
            Ajouter utilisateur
          </Button>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField
            placeholder="Rechercher un nom, pseudo ou e-mail…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flexGrow: 1, minWidth: 260 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Rôle</InputLabel>
            <Select label="Rôle" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <MenuItem value="">Tous les rôles</MenuItem>
              {ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={onlyDuplicates} onChange={(e) => setOnlyDuplicates(e.target.checked)} />}
            label={`Doublons possibles (${duplicateCount})`}
          />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {rows.length} / {users.length}
          </Typography>
        </Stack>
      </Paper>

      {onlyDuplicates && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Les comptes d'une même personne sont affichés côte à côte. Garde en général celui qui a
          des inscriptions ou qui vient de l'app ; avant de supprimer l'autre, vérifie qu'il ne
          s'agit pas de deux personnes différentes (par exemple sans date de naissance).
        </Alert>
      )}

      <div style={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={10}
          loading={loading}
          components={{ Toolbar: CustomToolbar }}
          sx={{
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#EDE9FE' },
            '& .MuiDataGrid-row:hover': { backgroundColor: '#F5F3FF' },
          }}
        />
      </div>
    </Box>
  );
}

export default Users;